/**
 * **Paksa-buka** — jalan keluar saat sebuah bill terkunci di perangkat yang sudah mati.
 *
 * Imron: *"jika ada kejadian table_in_use (karena device/hp mati) harus ada feature untuk paksa
 * buka table kembali (dengan warning). kalau di vtec bisa lewat kasir biasanya."*
 *
 * ### ⛔⛔ Kenapa dialognya menyebut PEMEGANG dan LAMANYA, bukan sekadar "sedang dipakai"
 * Keputusan yang sebenarnya diambil kasir di detik itu adalah **"perangkatnya mati atau orangnya
 * memang sedang bekerja?"** — dan satu-satunya bahan untuk menjawabnya adalah nama pemegang plus
 * sudah berapa lama. Dialog yang cuma bilang "terkunci" memaksa kasir menebak, dan tebakan yang
 * paling mudah selalu "rebut saja".
 *
 * ⚠️ Karena itu tombol rebutnya sengaja **bukan** tombol utama, dan kalimat pertama yang dibaca
 * kasir adalah anjuran MENUNGGU. Dua kasir yang menyunting satu bill menghasilkan dua layar yang
 * berbeda, dan yang menutupnya belakangan menimpa pekerjaan yang satunya tanpa sadar.
 *
 * ### ⭐ PIN diminta ULANG — sama seperti buka laci
 * Sesi staf hidup 8 jam; tanpa PIN, tombol ini mewarisi seluruh umurnya dan terminal yang
 * ditinggal sebentar cukup bagi siapa pun untuk merebut bill atas nama kasir. PIN di sini bukan
 * autentikasi (sesinya sudah sah) melainkan **bukti kehadiran**.
 *
 * ### ⛔ Layar tak menyembunyikan tombol berdasarkan izin
 * `order:takeover` ditegakkan SERVER. Menyembunyikan tombolnya di klien membuat orang yang tak
 * berizin melihat pintu yang berbeda dari pintu yang sebenarnya dijaga — dan saat kelak izinnya
 * diberikan, tak ada yang tahu kenapa tombolnya tak muncul.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';

/**
 * Alasan yang ditawarkan. ⛔ Enum kontrak, bukan teks bebas yang diketik kasir.
 *
 * ⚠️ Dua saja, dan itu memang seluruh kasusnya: perangkat pemegang MATI (`device_down`), atau
 * seseorang lupa melepas kunci — yang tak punya kode sendiri dan karena itu wajib berketerangan
 * (`other` + catatan). Menawarkan lebih banyak pilihan yang maknanya bertumpang tindih membuat dua
 * orang mencatat kejadian sama dengan kata berbeda.
 */
const REASONS = ['device_down', 'other'] as const;

export interface HoldConflict {
  readonly orderId: string;
  readonly heldByName: string | null;
  readonly heldSeconds: number | null;
  /** Aksi yang tadi ditolak — diulang otomatis sesudah kunci berpindah. */
  readonly retry: () => void | Promise<void>;
}

export interface TakeOverDialogProps {
  readonly conflict: HoldConflict;
  readonly deviceCode: string;
  readonly onClose: () => void;
}

export function TakeOverDialog({ conflict, deviceCode, onClose }: TakeOverDialogProps) {
  const { t } = useTranslation();
  const [pin, setPin] = useState('');
  const [reason, setReason] = useState<(typeof REASONS)[number]>('device_down');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ⛔ `other` tanpa penjelasan = teks bebas jilid dua. Layar menolak lebih dulu; server tetap jaring.
  const needsNote = reason === 'other' && note.trim() === '';
  const canTake = pin.length >= 4 && !needsNote && !busy;

  /*
   * ⚠️ Dibulatkan ke MENIT, dan di bawah semenit disebut dengan kata — bukan "0 menit".
   * "Dipegang 0 menit" terbaca sebagai "tak dipegang siapa pun", yang justru kebalikan artinya.
   */
  const secs = conflict.heldSeconds ?? 0;
  const mins = Math.floor(secs / 60);
  const lama = mins < 1 ? t('cashier.takeOver.lessThanMinute') : t('cashier.takeOver.minutes', { n: mins });

  return (
    <div
      className="dialog"
      role="dialog"
      aria-label={t('cashier.takeOver.title')}
      data-testid="takeover-dialog"
    >
      <h2>{t('cashier.takeOver.title')}</h2>

      {/* ⭐ Fakta dulu — nama & lama — supaya kasir bisa menilai, bukan sekadar menekan. */}
      <p data-testid="takeover-holder">
        {t('cashier.takeOver.heldBy', { name: conflict.heldByName ?? '?', duration: lama })}
      </p>

      {/* ⛔ Anjuran MENUNGGU dibaca lebih dulu daripada tombol merebut. */}
      <p className="dialog-note">{t('cashier.takeOver.waitFirst')}</p>

      <label>
        {t('cashier.takeOver.reason')}
        <select
          data-testid="takeover-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value as (typeof REASONS)[number])}
        >
          {REASONS.map((a) => (
            <option key={a} value={a}>{t(`enum.reasonCode.${a}`)}</option>
          ))}
        </select>
      </label>

      {reason === 'other' && (
        <label>
          {t('cashier.takeOver.note')}
          <input data-testid="takeover-note" value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
      )}

      <label>
        PIN
        <input
          type="password"
          inputMode="numeric"
          data-testid="takeover-pin"
          value={pin}
          onChange={(e) => setPin(e.target.value)}
        />
      </label>

      {/* ⚠️ Kasir DIBERI TAHU bahwa kejadiannya ditandai. Kontrol yang diam-diam menandai orang
          adalah kontrol yang, saat ketahuan, dianggap jebakan. */}
      <p className="dialog-note" data-testid="takeover-reviewed">{t('cashier.takeOver.reviewed')}</p>

      {error !== null && <p className="error" data-testid="takeover-error">{error}</p>}

      <div className="dialog-actions">
        <button type="button" className="action-primary" data-testid="takeover-cancel" onClick={onClose}>
          {t('cashier.takeOver.cancel')}
        </button>
        <button
          type="button"
          data-testid="takeover-confirm"
          disabled={!canTake}
          onClick={() => {
            setBusy(true);
            setError(null);
            void api
              .takeOver(conflict.orderId, {
                pin,
                reasonCode: reason,
                ...(note.trim() === '' ? {} : { reasonNote: note.trim() }),
                deviceCode,
              })
              /*
               * ⭐ Aksi yang tadi ditolak diulang OTOMATIS. Kunci berpindah bukan tujuan siapa
               * pun — yang diinginkan kasir adalah membuka bill itu. Menyuruhnya menekan lagi
               * hanya menambah satu langkah yang pasti dilakukannya.
               */
              .then(async () => {
                onClose();
                await conflict.retry();
              })
              .catch((e: Error) => { setError(e.message); })
              .finally(() => { setBusy(false); });
          }}
        >
          {t('cashier.takeOver.confirm')}
        </button>
      </div>
    </div>
  );
}
